import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideArrowRight } from '@ng-icons/lucide';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmTooltipTriggerDirective } from '@spartan-ng/helm/tooltip';
import { InlineNotice } from '@shared/components/inline-notice/inline-notice';
import { StepTimeline } from '@shared/components/step-timeline/step-timeline';
import { TimelineStep } from '@shared/models/step-timeline.model';
import { CampaignStore } from '@features/campaigns/store/campaign-store';
import { CampaignWorkflowStore } from '../../store/campaign-workflow.store';
import { CampaignArea } from '../campaign-area/campaign-area';
import { PHASES, STAGE_META, WorkflowStage } from '../../models/campaign-workflow.model';
import { areaInsights } from '../campaign-area/campaign-area.model';

const fmt = (n: number) => n.toLocaleString('de-DE');
const one = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const percent = (part: number, whole: number) => {
  if (!whole) return '0 %';
  const share = (part / whole) * 100;
  return `${share < 10 && part ? one(share) : Math.round(share)} %`;
};
/** Stages that start an execution; those wait while one is running. */
const STARTS_RUN: WorkflowStage[] = ['new', 'error', 'replan'];

@Component({
  selector: 'app-campaign-overview-tab',
  standalone: true,
  imports: [DecimalPipe, NgIconComponent, HlmButtonDirective, HlmTooltipTriggerDirective, InlineNotice, StepTimeline, CampaignArea],
  providers: [provideIcons({ lucideArrowRight })],
  templateUrl: './campaign-overview-tab.html',
  host: { class: 'flex flex-col gap-6' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignOverviewTab {
  readonly store = inject(CampaignWorkflowStore);
  private readonly campaignStore = inject(CampaignStore);
  readonly campaign = this.campaignStore.currentCampaign;
  readonly meta = STAGE_META;
  readonly timelineConfig = { variant: 'rail', showLines: true, showDescriptions: true, orientation: 'horizontal', animated: false, interactive: false, fitRail: true } as const;

  readonly isSettingUp = computed(() => {
    const c = this.campaign();
    return !!c && !c.campaignSiId && !c.metadata?.['setupFailed'];
  });
  readonly setupFailed = computed(() => {
    const c = this.campaign();
    return !!c && !c.campaignSiId && c.metadata?.['setupFailed'] === true;
  });
  readonly setupError = computed(
    () => (this.campaign()?.metadata?.['setupError'] as string | undefined) ?? 'Die Einrichtung in SolarInsight ist fehlgeschlagen.',
  );

  readonly kpis = computed(() => areaInsights(this.store.items() ?? []));

  constructor() {
    void this.store.loadItems();
  }

  readonly phases = computed(() => {
    const counts = this.store.counts();
    const summary = this.store.summary();
    return PHASES.map((phase, index) => {
      const sum = phase.stages.reduce((all, stage) => all + (counts?.[stage] ?? 0), 0);
      return {
        name: phase.name,
        number: index + 1,
        sum,
        // Nothing left here and work has moved on: the only state the rail needs to show.
        done: !!counts && index < PHASES.length - 1 && sum === 0 && PHASES.slice(index + 1).some((later) => later.stages.some((stage) => counts[stage])),
        rows: phase.stages.map((stage) => {
          const count = counts?.[stage] ?? 0;
          return {
            stage,
            meta: STAGE_META[stage],
            count,
            share: percent(count, this.store.total()),
            detail: this.detail(stage, summary),
            action: count && STAGE_META[stage].action && !(this.store.running() && STARTS_RUN.includes(stage)) ? STAGE_META[stage].action! : null,
          };
        }),
      };
    });
  });

  /** The four phases on the same rail as the steps of an execution. */
  readonly steps = computed<TimelineStep[]>(() =>
    this.phases().map((phase) => ({
      id: phase.name,
      label: phase.name,
      state: phase.done ? 'completed' : 'upcoming',
      nodeText: phase.done ? undefined : String(phase.number),
      description: `${fmt(phase.sum)} ${phase.sum === 1 ? 'Analyse' : 'Analysen'}`,
      clickable: false,
    })),
  );

  /** The row does what its label says; without an action it opens the list. */
  openRow(stage: WorkflowStage, action: string | null): void {
    if (action) this.store.act(stage);
    else void this.store.go('analyses', { stage });
  }

  private detail(stage: WorkflowStage, summary: ReturnType<CampaignWorkflowStore['summary']>): string | null {
    if (stage === 'review' && summary?.plannedInReview) return `Davon ${fmt(summary.plannedInReview)} schon geplant`;
    if (stage === 'replan' && summary) return `${fmt(summary.replanCorrected)} korrigiert · ${fmt(summary.replanWithoutPlanning)} ohne Planung`;
    if (stage === 'done' && summary?.recheck) return `${fmt(summary.recheck)} nachgeplant, noch nicht wieder angesehen`;
    return null;
  }
}
